/**
 * Currency configuration object with extended currencies
 */
const currencyConfig = {
    USD: { symbol: '$', position: 'before', locale: 'en-US', decimalDigits: 2 },
    EUR: { symbol: '€', position: 'after', locale: 'de-DE', decimalDigits: 2, space: true },
    GBP: { symbol: '£', position: 'before', locale: 'en-GB', decimalDigits: 2 },
    JPY: { symbol: '¥', position: 'before', locale: 'ja-JP', decimalDigits: 0 },
    CHF: { symbol: 'CHF', position: 'before', locale: 'de-CH', decimalDigits: 2, space: true },
    CAD: { symbol: 'CA$', position: 'before', locale: 'en-CA', decimalDigits: 2 },
    AUD: { symbol: 'AU$', position: 'before', locale: 'en-AU', decimalDigits: 2 },
    NZD: { symbol: 'NZ$', position: 'before', locale: 'en-NZ', decimalDigits: 2 },
    CNY: { symbol: 'CN¥', position: 'before', locale: 'zh-CN', decimalDigits: 2 },
    HKD: { symbol: 'HK$', position: 'before', locale: 'zh-HK', decimalDigits: 2 },
    SGD: { symbol: 'S$', position: 'before', locale: 'en-SG', decimalDigits: 2 },
    SEK: { symbol: 'kr', position: 'after', locale: 'sv-SE', decimalDigits: 2, space: true },
    NOK: { symbol: 'kr', position: 'after', locale: 'nb-NO', decimalDigits: 2, space: true },
    DKK: { symbol: 'kr.', position: 'after', locale: 'da-DK', decimalDigits: 2, space: true },
    INR: { symbol: '₹', position: 'before', locale: 'en-IN', decimalDigits: 2 },
    BRL: { symbol: 'R$', position: 'before', locale: 'pt-BR', decimalDigits: 2 },
    MXN: { symbol: 'MX$', position: 'before', locale: 'es-MX', decimalDigits: 2 },
    ZAR: { symbol: 'R', position: 'before', locale: 'en-ZA', decimalDigits: 2 },
    TRY: { symbol: '₺', position: 'before', locale: 'tr-TR', decimalDigits: 2 },
    RUB: { symbol: '₽', position: 'after', locale: 'ru-RU', decimalDigits: 2, space: true },
    AED: { symbol: 'د.إ', position: 'before', locale: 'ar-AE', decimalDigits: 2 },
    SAR: { symbol: 'ر.س', position: 'before', locale: 'ar-SA', decimalDigits: 2 },
    ILS: { symbol: '₪', position: 'before', locale: 'he-IL', decimalDigits: 2 },
    MYR: { symbol: 'RM', position: 'before', locale: 'ms-MY', decimalDigits: 2 },
    THB: { symbol: '฿', position: 'before', locale: 'th-TH', decimalDigits: 2 },
    PHP: { symbol: '₱', position: 'before', locale: 'en-PH', decimalDigits: 2 },
    IDR: { symbol: 'Rp', position: 'before', locale: 'id-ID', decimalDigits: 0 },
    VND: { symbol: '₫', position: 'after', locale: 'vi-VN', decimalDigits: 0 }
};

/**
 * List of available currencies for UI selection
 */
export const currencies = [
    { text: 'US Dollar (USD)', value: 'USD' },
    { text: 'Euro (EUR)', value: 'EUR' },
    { text: 'British Pound (GBP)', value: 'GBP' },
    { text: 'Japanese Yen (JPY)', value: 'JPY' },
    { text: 'Swiss Franc (CHF)', value: 'CHF' },
    { text: 'Canadian Dollar (CAD)', value: 'CAD' },
    { text: 'Australian Dollar (AUD)', value: 'AUD' },
    { text: 'New Zealand Dollar (NZD)', value: 'NZD' },
    { text: 'Chinese Yuan (CNY)', value: 'CNY' },
    { text: 'Hong Kong Dollar (HKD)', value: 'HKD' },
    { text: 'Singapore Dollar (SGD)', value: 'SGD' },
    { text: 'Swedish Krona (SEK)', value: 'SEK' },
    { text: 'Norwegian Krone (NOK)', value: 'NOK' },
    { text: 'Danish Krone (DKK)', value: 'DKK' },
    { text: 'Indian Rupee (INR)', value: 'INR' },
    { text: 'Brazilian Real (BRL)', value: 'BRL' },
    { text: 'Mexican Peso (MXN)', value: 'MXN' },
    { text: 'South African Rand (ZAR)', value: 'ZAR' },
    { text: 'Turkish Lira (TRY)', value: 'TRY' },
    { text: 'Russian Ruble (RUB)', value: 'RUB' },
    { text: 'UAE Dirham (AED)', value: 'AED' },
    { text: 'Saudi Riyal (SAR)', value: 'SAR' },
    { text: 'Israeli Shekel (ILS)', value: 'ILS' },
    { text: 'Malaysian Ringgit (MYR)', value: 'MYR' },
    { text: 'Thai Baht (THB)', value: 'THB' },
    { text: 'Philippine Peso (PHP)', value: 'PHP' },
    { text: 'Indonesian Rupiah (IDR)', value: 'IDR' },
    { text: 'Vietnamese Dong (VND)', value: 'VND' },
];

/**
 * Remove unnecessary trailing zeros in decimals
 * @param {number|string} price - The price value
 * @returns {string} - Price without unnecessary decimals
 */
/**
 * Remove unnecessary trailing zeros in decimals
 * @param {number|string} price - The price value
 * @returns {string} - Price without unnecessary decimals
 */
function removeUnnecessaryDecimals(price) {
    const priceStr = price.toString();
    return priceStr.indexOf('.') === -1 ? priceStr : priceStr.replace(/\.?0+$/, '');
}


/**
 * Basic price formatting without symbols
 * @param {number} price - The price value
 * @returns {string} - Formatted price
 */
export function plainPrice(price) {
    if (typeof price === 'undefined' || price === null) {
        return '';
    }
    return removeUnnecessaryDecimals(parseFloat(price).toFixed(2));
}

/**
 * Format currency value
 * @param {number} value - Value to format
 * @param {string} [currencyCode='USD'] - Currency code
 * @param {boolean} [hideDecimals=false] - Whether to hide decimals
 * @returns {string} - Formatted currency string
 */
export function formatCurrency(value, currencyCode = 'USD', hideDecimals = false) {
    if (typeof value === 'undefined' || value === null) return '';
    
    const config = currencyConfig[currencyCode] || currencyConfig.USD;
    const options = {
        style: 'decimal',
        minimumFractionDigits: hideDecimals ? 0 : config.decimalDigits,
        maximumFractionDigits: hideDecimals ? 0 : config.decimalDigits
    };
    
    const formatted = new Intl.NumberFormat(config.locale, options).format(value);
    const space = config.space ? ' ' : '';
    
    return config.position === 'before'
        ? `${config.symbol}${space}${formatted}`
        : `${formatted}${space}${config.symbol}`;
}

/**
 * Get currency symbol for a given currency code
 * @param {string} [currencyCode='USD'] - Currency code
 * @returns {string} - Currency symbol
 */
export function getCurrencySymbol(currencyCode = 'USD') {
    const config = currencyConfig[currencyCode] || currencyConfig.USD;
    return config.symbol;
}

/**
 * Format name with proper capitalization
 * @param {string} name - Name to format
 * @returns {string} - Formatted name
 */
export function formatName(name) {
    if (!name) return '';

    return name
        .toLowerCase()
        .split(' ')
        .map(word => 
            word
                .split('-')
                .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                .join('-')
        )
        .map(word => 
            word.endsWith('.') ? word.toUpperCase() : word
        )
        .join(' ');
}

/**
 * Format date according to specified format
 * @param {string|Date} date - Date to format
 * @param {string} [format='yyyy-mm-dd'] - Desired format
 * @returns {string} - Formatted date
 */
export function formatDate(date, format = 'yyyy-mm-dd') {
    if (!date || date === '0000-00-00') return '';

    const pdate = new Date(date);
    if (isNaN(pdate.getTime())) return '';

    const day = String(pdate.getDate()).padStart(2, '0');
    const month = String(pdate.getMonth() + 1).padStart(2, '0');
    const year = pdate.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    switch (format.toLowerCase()) {
        case 'dd-mm-yyyy': return `${day}-${month}-${year}`;
        case 'dd/mm/yyyy': return `${day}/${month}/${year}`;
        case 'mm/dd/yyyy': return `${month}/${day}/${year}`;
        case 'mmm dd, yyyy': return `${monthNames[pdate.getMonth()]} ${day}, ${year}`;
        case 'ddd, mmm dd, yyyy': return `${dayNames[pdate.getDay()]}, ${monthNames[pdate.getMonth()]} ${day}, ${year}`;
        case 'yyyy-mm-dd':
        default: return `${year}-${month}-${day}`;
    }
}

/**
 * Get image URL with fallback
 * @param {string} image - Image path
 * @param {string} [url] - Base URL
 * @param {string} [defaultImage='/no-picture-available.png'] - Default fallback image
 * @returns {string} - Complete image URL
 */
export function getImage(image, url, defaultImage = '/no-picture-available.png') {
    if (!image) return defaultImage;
    
    if (url) {
        try {
            const imageUrl = new URL(image, url);
            return imageUrl.toString();
        } catch (e) {
            console.warn('Invalid image URL:', e);
            return defaultImage;
        }
    }
    
    return image || defaultImage;
}